/**
 * Workflow 설정 페이지
 * @description Workflow 서비스 유형·Workflow 서비스(요청 유형)·요청 유형별 사용 대상·결재 플로우 설정
 * @see docs/Workflow서비스설정-기획.md
 */

import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@shared/ui/primitives';
import { Plus, Pencil, Trash2, GitBranch, Layers, ListChecks, Workflow } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/utils/httpClient';

export interface WorkflowServiceType {
  serviceTypeId: string;
  displayName: string;
  description?: string;
  linkedFolder?: string;
  displayOrder?: number;
  enabledInDomains?: ('servicerequest' | 'incident' | 'changerequest')[];
}

interface ServiceTypesResponse {
  schemaVersion: string;
  tenantId: string;
  generatedAt: string;
  serviceTypes: WorkflowServiceType[];
  fileVersion?: number;
}

const DOMAIN_LABELS: Record<string, string> = {
  servicerequest: '서비스 요청',
  incident: '장애 관리',
  changerequest: '변경 관리',
};

export type DomainType = 'servicerequest' | 'incident' | 'changerequest';

export interface FormFieldDef {
  fieldId: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'number' | 'date' | 'checkbox';
  required?: boolean;
  options?: string[];
  placeholder?: string;
}

export interface RequestTypeItem {
  requestTypeId: string;
  domain: DomainType;
  category: string;
  displayName: string;
  description?: string;
  workflowServiceTypeId?: string;
  form: { fields: FormFieldDef[] };
  approvalFlowId?: string;
  enabledInDomains?: DomainType[];
}

interface RequestTypesResponse {
  schemaVersion: string;
  tenantId: string;
  generatedAt: string;
  requestTypes: RequestTypeItem[];
  fileVersion?: number;
}

export interface ApprovalStepDef {
  approvers?: { kind: 'user' | 'team'; userId?: string; teamId?: string }[];
  consensus?: { kind: 'user' | 'team'; userId?: string; teamId?: string }[];
  notify?: { kind: 'user' | 'team'; userId?: string; teamId?: string }[];
}

export interface ApprovalFlowItem {
  approvalFlowId: string;
  name?: string;
  steps?: ApprovalStepDef[];
}

interface ApprovalFlowsResponse {
  schemaVersion: string;
  tenantId: string;
  generatedAt: string;
  approvalFlows: ApprovalFlowItem[];
  fileVersion?: number;
}

interface OrgResponse {
  teams: { teamId: string; teamName: string; status: string }[];
  users: { userId: string; displayName: string; email: string; position?: string; teamIds: string[]; status: string }[];
  fileVersion?: number;
}

type WorkflowFileKey =
  | 'workflows/service-types.json'
  | 'workflows/request-types.json'
  | 'workflows/approval-flows.json'
  | 'org.json';

type PortalWriteJobStatus = 'queued' | 'processing' | 'succeeded' | 'failed';

interface PortalWriteJobData {
  status: PortalWriteJobStatus;
  newVersion?: number | null;
  error?: string | null;
}

export interface WorkflowSettingsPageProps {
  /** 왼쪽 메뉴 모드: 지정 시 탭 대신 해당 섹션만 렌더 */
  activeSection?: string;
  onSectionChange?: (sectionId: string) => void;
}

export const WorkflowSettingsPage: React.FC<WorkflowSettingsPageProps> = ({ activeSection: activeSectionProp, onSectionChange }) => {
  const { user } = useAuthStore();
  const [tenantId, setTenantId] = useState<string>('');
  const [tenantOptions, setTenantOptions] = useState<Array<{ tenantId: string; tenantName: string }>>([]);
  const [serviceTypes, setServiceTypes] = useState<WorkflowServiceType[]>([]);
  const [requestTypes, setRequestTypes] = useState<RequestTypeItem[]>([]);
  const [approvalFlows, setApprovalFlows] = useState<ApprovalFlowItem[]>([]);
  const [org, setOrg] = useState<OrgResponse | null>(null);
  const [fileVersions, setFileVersions] = useState<Record<WorkflowFileKey, number>>({
    'workflows/service-types.json': 0,
    'workflows/request-types.json': 0,
    'workflows/approval-flows.json': 0,
    'org.json': 0,
  });
  const [activeTab, setActiveTab] = useState<string>('service-types');
  const [isLoading, setIsLoading] = useState(false);
  const isLeftMenuMode = activeSectionProp != null;
  const activeSection = isLeftMenuMode ? activeSectionProp : activeTab;
  const setActiveSection = isLeftMenuMode ? (onSectionChange ?? (() => {})) : setActiveTab;

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<WorkflowServiceType>({
    serviceTypeId: '',
    displayName: '',
    description: '',
    linkedFolder: '',
    displayOrder: 0,
    enabledInDomains: ['servicerequest', 'incident', 'changerequest'],
  });

  const [isRequestTypeDialogOpen, setIsRequestTypeDialogOpen] = useState(false);
  const [editingRequestTypeIndex, setEditingRequestTypeIndex] = useState<number | null>(null);
  const [requestTypeForm, setRequestTypeForm] = useState<RequestTypeItem>({
    requestTypeId: '',
    domain: 'servicerequest',
    category: '',
    displayName: '',
    description: '',
    workflowServiceTypeId: '',
    form: { fields: [] },
    approvalFlowId: '',
    enabledInDomains: ['servicerequest', 'incident', 'changerequest'],
  });

  const [isApprovalFlowDialogOpen, setIsApprovalFlowDialogOpen] = useState(false);
  const [editingApprovalFlowIndex, setEditingApprovalFlowIndex] = useState<number | null>(null);
  const [approvalFlowForm, setApprovalFlowForm] = useState<ApprovalFlowItem>({
    approvalFlowId: '',
    name: '',
    steps: [{ approvers: [], consensus: [], notify: [] }],
  });

  useEffect(() => {
    if (user?.userType === 'tenant-user' && user.tenantId) {
      setTenantId(user.tenantId);
      return;
    }
    if (user?.userType === 'portal-admin') {
      apiClient
        .get<{ tenants: Array<{ tenantId: string; tenantName: string }> }>('/admin/tenants')
        .then((data) => {
          const tenants = data.tenants || [];
          setTenantOptions(tenants);
          const saved = localStorage.getItem('iampam_admin_workflow_tenantId') || '';
          const initial = (saved && tenants.some((t) => t.tenantId === saved) && saved) || tenants[0]?.tenantId || '';
          setTenantId(initial);
        })
        .catch(() => {
          setTenantOptions([]);
          setTenantId('');
        });
    }
  }, [user?.userType, (user as any)?.tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    if (user?.userType === 'portal-admin') {
      localStorage.setItem('iampam_admin_workflow_tenantId', tenantId);
    }
    loadAllWorkflowData(tenantId);
  }, [tenantId]);

  const loadAllWorkflowData = async (targetTenantId: string) => {
    try {
      setIsLoading(true);
      const [stRes, rtRes, afRes, orgRes] = await Promise.all([
        apiClient.get<ServiceTypesResponse>(`/admin/tenants/${encodeURIComponent(targetTenantId)}/workflows/service-types`).catch(() => ({ serviceTypes: [] })),
        apiClient.get<RequestTypesResponse>(`/admin/tenants/${encodeURIComponent(targetTenantId)}/workflows/request-types`).catch(() => ({ requestTypes: [] })),
        apiClient.get<ApprovalFlowsResponse>(`/admin/tenants/${encodeURIComponent(targetTenantId)}/workflows/approval-flows`).catch(() => ({ approvalFlows: [] })),
        apiClient.get<OrgResponse>(`/admin/tenants/${encodeURIComponent(targetTenantId)}/org`).catch(() => null),
      ]);
      setServiceTypes(Array.isArray(stRes.serviceTypes) ? stRes.serviceTypes : []);
      setRequestTypes(Array.isArray((rtRes as any).requestTypes) ? (rtRes as any).requestTypes : []);
      setApprovalFlows(Array.isArray((afRes as any).approvalFlows) ? (afRes as any).approvalFlows : []);
      setOrg(orgRes && typeof orgRes === 'object' && Array.isArray((orgRes as any).teams) ? (orgRes as OrgResponse) : null);
      setFileVersions((prev) => ({
        'workflows/service-types.json': Number((stRes as any)?.fileVersion ?? prev['workflows/service-types.json'] ?? 0),
        'workflows/request-types.json': Number((rtRes as any)?.fileVersion ?? prev['workflows/request-types.json'] ?? 0),
        'workflows/approval-flows.json': Number((afRes as any)?.fileVersion ?? prev['workflows/approval-flows.json'] ?? 0),
        'org.json': Number((orgRes as any)?.fileVersion ?? prev['org.json'] ?? 0),
      }));
    } catch (e) {
      console.error('Failed to load workflow data:', e);
      setServiceTypes([]);
      setRequestTypes([]);
      setApprovalFlows([]);
      setOrg(null);
    } finally {
      setIsLoading(false);
    }
  };

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const normalizePollPath = (pollUrl: string) => {
    if (pollUrl.startsWith('http')) return new URL(pollUrl).pathname.replace(/^\/api\/portal/, '');
    return pollUrl.replace(/^\/api\/portal/, '');
  };

  const pollPortalWriteJob = async (pollUrl: string): Promise<PortalWriteJobData> => {
    const path = normalizePollPath(pollUrl);
    for (let i = 0; i < 30; i++) {
      await sleep(2000);
      const pollRes = await apiClient.get<any>(path);
      const job = (pollRes?.data ?? pollRes) as PortalWriteJobData;
      if (job?.status === 'succeeded') return job;
      if (job?.status === 'failed') {
        throw new Error(job?.error || '비동기 저장 작업이 실패했습니다.');
      }
    }
    throw new Error('비동기 저장 작업 폴링이 시간 초과되었습니다.');
  };

  const saveTenantConfigAsyncAware = async (
    endpoint: string,
    fileKey: WorkflowFileKey,
    payload: Record<string, unknown>,
  ) => {
    try {
      const response = await apiClient.put<any>(endpoint, {
        ...payload,
        baseVersion: fileVersions[fileKey] ?? 0,
      });

      const queued = response?.success === true && response?.data?.pollUrl;
      if (queued) {
        const job = await pollPortalWriteJob(response.data.pollUrl);
        if (typeof job?.newVersion === 'number') {
          setFileVersions((prev) => ({ ...prev, [fileKey]: job.newVersion as number }));
        }
      } else if (typeof response?.fileVersion === 'number') {
        setFileVersions((prev) => ({ ...prev, [fileKey]: response.fileVersion as number }));
      }

      await loadAllWorkflowData(tenantId);
      return true;
    } catch (err: any) {
      const isConflict = err?.status === 409
        || String(err?.message || '').toLowerCase().includes('baseversion');
      if (isConflict) {
        alert('다른 사용자가 먼저 수정했습니다. 최신 데이터로 다시 시도해 주세요.');
        await loadAllWorkflowData(tenantId);
        return false;
      }
      throw err;
    }
  };

  const openAddDialog = () => {
    setEditingIndex(null);
    setFormData({
      serviceTypeId: '',
      displayName: '',
      description: '',
      linkedFolder: '',
      displayOrder: serviceTypes.length,
      enabledInDomains: ['servicerequest', 'incident', 'changerequest'],
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (index: number) => {
    const item = serviceTypes[index];
    setEditingIndex(index);
    setFormData({
      serviceTypeId: item.serviceTypeId,
      displayName: item.displayName,
      description: item.description || '',
      linkedFolder: item.linkedFolder || '',
      displayOrder: item.displayOrder ?? index,
      enabledInDomains: item.enabledInDomains ?? ['servicerequest', 'incident', 'changerequest'],
    });
    setIsDialogOpen(true);
  };

  const handleSaveServiceType = async () => {
    if (!tenantId || !formData.serviceTypeId?.trim() || !formData.displayName?.trim()) return;
    const list = [...serviceTypes];
    const payload: WorkflowServiceType = {
      serviceTypeId: formData.serviceTypeId.trim(),
      displayName: formData.displayName.trim(),
      description: formData.description?.trim() || undefined,
      linkedFolder: formData.linkedFolder?.trim() || undefined,
      displayOrder: formData.displayOrder ?? list.length,
      enabledInDomains: formData.enabledInDomains?.length ? formData.enabledInDomains : undefined,
    };
    if (editingIndex !== null) {
      list[editingIndex] = payload;
    } else {
      if (list.some((s) => s.serviceTypeId === payload.serviceTypeId)) {
        alert('동일한 유형 ID가 이미 있습니다.');
        return;
      }
      list.push(payload);
    }
    try {
      setIsLoading(true);
      const ok = await saveTenantConfigAsyncAware(
        `/admin/tenants/${encodeURIComponent(tenantId)}/workflows/service-types`,
        'workflows/service-types.json',
        { schemaVersion: '2.0.0', tenantId, generatedAt: new Date().toISOString(), serviceTypes: list }
      );
      if (!ok) return;
      setIsDialogOpen(false);
    } catch (err) {
      console.error('Failed to save workflow service types:', err);
      alert('저장에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteServiceType = async (index: number) => {
    if (!tenantId || !confirm('이 Workflow 서비스 유형을 삭제하시겠습니까?')) return;
    const list = serviceTypes.filter((_, i) => i !== index);
    try {
      setIsLoading(true);
      const ok = await saveTenantConfigAsyncAware(
        `/admin/tenants/${encodeURIComponent(tenantId)}/workflows/service-types`,
        'workflows/service-types.json',
        { schemaVersion: '2.0.0', tenantId, generatedAt: new Date().toISOString(), serviceTypes: list }
      );
      if (!ok) return;
    } catch (err) {
      console.error('Failed to delete:', err);
      alert('삭제에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const openRequestTypeAdd = () => {
    setEditingRequestTypeIndex(null);
    setRequestTypeForm({
      requestTypeId: '',
      domain: 'servicerequest',
      category: '',
      displayName: '',
      description: '',
      workflowServiceTypeId: '',
      form: { fields: [] },
      approvalFlowId: '',
      enabledInDomains: ['servicerequest', 'incident', 'changerequest'],
    });
    setIsRequestTypeDialogOpen(true);
  };

  const openRequestTypeEdit = (index: number) => {
    const item = requestTypes[index];
    setEditingRequestTypeIndex(index);
    setRequestTypeForm({
      requestTypeId: item.requestTypeId,
      domain: item.domain || 'servicerequest',
      category: item.category || '',
      displayName: item.displayName || '',
      description: item.description || '',
      workflowServiceTypeId: item.workflowServiceTypeId || '',
      form: item.form && Array.isArray(item.form.fields) ? { fields: [...item.form.fields] } : { fields: [] },
      approvalFlowId: item.approvalFlowId || '',
      enabledInDomains: item.enabledInDomains ?? ['servicerequest', 'incident', 'changerequest'],
    });
    setIsRequestTypeDialogOpen(true);
  };

  const handleSaveRequestType = async () => {
    if (!tenantId || !requestTypeForm.requestTypeId?.trim() || !requestTypeForm.displayName?.trim()) return;
    const list = [...requestTypes];
    const payload: RequestTypeItem = {
      requestTypeId: requestTypeForm.requestTypeId.trim(),
      domain: requestTypeForm.domain,
      category: requestTypeForm.category?.trim() || '',
      displayName: requestTypeForm.displayName.trim(),
      description: requestTypeForm.description?.trim() || undefined,
      workflowServiceTypeId: requestTypeForm.workflowServiceTypeId?.trim() || undefined,
      form: requestTypeForm.form?.fields?.length ? { fields: requestTypeForm.form.fields } : { fields: [] },
      approvalFlowId: requestTypeForm.approvalFlowId?.trim() || undefined,
      enabledInDomains: requestTypeForm.enabledInDomains?.length ? requestTypeForm.enabledInDomains : undefined,
    };
    if (editingRequestTypeIndex !== null) {
      list[editingRequestTypeIndex] = payload;
    } else {
      if (list.some((r) => r.requestTypeId === payload.requestTypeId)) {
        alert('동일한 요청 유형 ID가 이미 있습니다.');
        return;
      }
      list.push(payload);
    }
    try {
      setIsLoading(true);
      const ok = await saveTenantConfigAsyncAware(
        `/admin/tenants/${encodeURIComponent(tenantId)}/workflows/request-types`,
        'workflows/request-types.json',
        { schemaVersion: '2.0.0', tenantId, generatedAt: new Date().toISOString(), requestTypes: list }
      );
      if (!ok) return;
      setIsRequestTypeDialogOpen(false);
    } catch (err) {
      console.error('Failed to save request types:', err);
      alert('저장에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteRequestType = async (index: number) => {
    if (!tenantId || !confirm('이 요청 유형을 삭제하시겠습니까?')) return;
    const list = requestTypes.filter((_, i) => i !== index);
    try {
      setIsLoading(true);
      const ok = await saveTenantConfigAsyncAware(
        `/admin/tenants/${encodeURIComponent(tenantId)}/workflows/request-types`,
        'workflows/request-types.json',
        { schemaVersion: '2.0.0', tenantId, generatedAt: new Date().toISOString(), requestTypes: list }
      );
      if (!ok) return;
    } catch (err) {
      console.error('Failed to delete request type:', err);
      alert('삭제에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const openApprovalFlowAdd = () => {
    setEditingApprovalFlowIndex(null);
    setApprovalFlowForm({
      approvalFlowId: '',
      name: '',
      steps: [{ approvers: [], consensus: [], notify: [] }],
    });
    setIsApprovalFlowDialogOpen(true);
  };

  const openApprovalFlowEdit = (index: number) => {
    const item = approvalFlows[index];
    setEditingApprovalFlowIndex(index);
    setApprovalFlowForm({
      approvalFlowId: item.approvalFlowId,
      name: item.name || '',
      steps: Array.isArray(item.steps) && item.steps.length
        ? item.steps.map((s) => ({
            approvers: s.approvers ?? [],
            consensus: s.consensus ?? [],
            notify: s.notify ?? [],
          }))
        : [{ approvers: [], consensus: [], notify: [] }],
    });
    setIsApprovalFlowDialogOpen(true);
  };

  const handleSaveApprovalFlow = async () => {
    if (!tenantId || !approvalFlowForm.approvalFlowId?.trim()) return;
    const list = [...approvalFlows];
    const payload: ApprovalFlowItem = {
      approvalFlowId: approvalFlowForm.approvalFlowId.trim(),
      name: approvalFlowForm.name?.trim() || undefined,
      steps: approvalFlowForm.steps?.length ? approvalFlowForm.steps : undefined,
    };
    if (editingApprovalFlowIndex !== null) {
      list[editingApprovalFlowIndex] = payload;
    } else {
      if (list.some((a) => a.approvalFlowId === payload.approvalFlowId)) {
        alert('동일한 결재 플로우 ID가 이미 있습니다.');
        return;
      }
      list.push(payload);
    }
    try {
      setIsLoading(true);
      const ok = await saveTenantConfigAsyncAware(
        `/admin/tenants/${encodeURIComponent(tenantId)}/workflows/approval-flows`,
        'workflows/approval-flows.json',
        { schemaVersion: '2.0.0', tenantId, generatedAt: new Date().toISOString(), approvalFlows: list }
      );
      if (!ok) return;
      setIsApprovalFlowDialogOpen(false);
    } catch (err) {
      console.error('Failed to save approval flows:', err);
      alert('저장에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteApprovalFlow = async (index: number) => {
    if (!tenantId || !confirm('이 결재 플로우를 삭제하시겠습니까?')) return;
    const list = approvalFlows.filter((_, i) => i !== index);
    try {
      setIsLoading(true);
      const ok = await saveTenantConfigAsyncAware(
        `/admin/tenants/${encodeURIComponent(tenantId)}/workflows/approval-flows`,
        'workflows/approval-flows.json',
        { schemaVersion: '2.0.0', tenantId, generatedAt: new Date().toISOString(), approvalFlows: list }
      );
      if (!ok) return;
    } catch (err) {
      console.error('Failed to delete approval flow:', err);
      alert('삭제에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleServiceTypeDomain = (typeIndex: number, domain: DomainType) => {
    const list = [...serviceTypes];
    const item = list[typeIndex];
    const current = item.enabledInDomains ?? [];
    const next = current.includes(domain)
      ? current.filter((d) => d !== domain)
      : [...current, domain];
    list[typeIndex] = { ...item, enabledInDomains: next.length ? next : undefined };
    setServiceTypes(list);
  };

  const toggleRequestTypeDomain = (typeIndex: number, domain: DomainType) => {
    const list = [...requestTypes];
    const item = list[typeIndex];
    const current = item.enabledInDomains ?? [];
    const next = current.includes(domain)
      ? current.filter((d) => d !== domain)
      : [...current, domain];
    list[typeIndex] = { ...item, enabledInDomains: next.length ? next : undefined };
    setRequestTypes(list);
  };

  const saveDomainVisibility = async () => {
    if (!tenantId) return;
    try {
      setIsLoading(true);
      const [ok1, ok2] = await Promise.all([
        saveTenantConfigAsyncAware(
          `/admin/tenants/${encodeURIComponent(tenantId)}/workflows/service-types`,
          'workflows/service-types.json',
          { schemaVersion: '2.0.0', tenantId, generatedAt: new Date().toISOString(), serviceTypes }
        ),
        saveTenantConfigAsyncAware(
          `/admin/tenants/${encodeURIComponent(tenantId)}/workflows/request-types`,
          'workflows/request-types.json',
          { schemaVersion: '2.0.0', tenantId, generatedAt: new Date().toISOString(), requestTypes }
        ),
      ]);
      if (!ok1 || !ok2) return;
      alert('저장되었습니다.');
    } catch (err) {
      console.error('Failed to save domain visibility:', err);
      alert('저장에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Workflow 설정</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Workflow 서비스 유형·요청 유형·결재 플로우·요청 유형별 사용 대상을 설정합니다.
          </p>
        </div>
        {user?.userType === 'portal-admin' && tenantOptions.length > 0 && (
          <Select value={tenantId} onValueChange={setTenantId}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Tenant 선택" />
            </SelectTrigger>
            <SelectContent>
              {tenantOptions.map((t) => (
                <SelectItem key={t.tenantId} value={t.tenantId}>
                  {t.tenantName || t.tenantId}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <Tabs value={activeSection} onValueChange={setActiveSection}>
        {!isLeftMenuMode && (
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="service-types" className="gap-2">
            <Layers className="w-4 h-4" />
            Workflow 서비스 유형
          </TabsTrigger>
          <TabsTrigger value="request-types" className="gap-2">
            <ListChecks className="w-4 h-4" />
            Workflow 서비스
          </TabsTrigger>
          <TabsTrigger value="domain-visibility" className="gap-2">
            <ListChecks className="w-4 h-4" />
            요청 유형별 사용 대상
          </TabsTrigger>
          <TabsTrigger value="approval-flows" className="gap-2">
            <Workflow className="w-4 h-4" />
            결재 플로우
          </TabsTrigger>
        </TabsList>
        )}

        <TabsContent value="service-types" className="space-y-4 mt-4">
          <Card className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Workflow 서비스 유형</h2>
              <Button onClick={openAddDialog} className="gap-2" disabled={!tenantId || isLoading}>
                <Plus className="w-4 h-4" />
                유형 추가
              </Button>
            </div>
            {isLoading && !serviceTypes.length ? (
              <p className="text-sm text-muted-foreground py-8 text-center">로딩 중...</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>유형 ID</TableHead>
                    <TableHead>표시명</TableHead>
                    <TableHead>설명</TableHead>
                    <TableHead>연결 폴더</TableHead>
                    <TableHead>순서</TableHead>
                    <TableHead>사용 대상</TableHead>
                    <TableHead className="w-[100px]">작업</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {serviceTypes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        유형이 없습니다. &quot;유형 추가&quot;로 등록하세요.
                      </TableCell>
                    </TableRow>
                  ) : (
                    [...serviceTypes]
                      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                      .map((row, idx) => (
                        <TableRow key={row.serviceTypeId}>
                          <TableCell className="font-mono text-sm">{row.serviceTypeId}</TableCell>
                          <TableCell>{row.displayName}</TableCell>
                          <TableCell className="max-w-[200px] truncate text-muted-foreground">
                            {row.description || '-'}
                          </TableCell>
                          <TableCell className="font-mono text-xs">{row.linkedFolder || '-'}</TableCell>
                          <TableCell>{row.displayOrder ?? '-'}</TableCell>
                          <TableCell>
                            {(row.enabledInDomains?.length
                              ? row.enabledInDomains
                              : ['servicerequest', 'incident', 'changerequest']
                            ).map((d) => (
                              <span
                                key={d}
                                className="inline-block mr-1 px-1.5 py-0.5 rounded text-xs bg-primary/10 text-primary"
                              >
                                {DOMAIN_LABELS[d] || d}
                              </span>
                            ))}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => openEditDialog(serviceTypes.indexOf(row))}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteServiceType(serviceTypes.indexOf(row))}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="request-types" className="mt-4">
          <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">Workflow 서비스(요청 유형) 목록을 관리합니다.</p>
              <Button size="sm" onClick={openRequestTypeAdd} disabled={!tenantId || isLoading}>
                <Plus className="w-4 h-4 mr-1" />
                추가
              </Button>
            </div>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>요청 유형 ID</TableHead>
                    <TableHead>표시명</TableHead>
                    <TableHead>도메인</TableHead>
                    <TableHead>Workflow 서비스 유형</TableHead>
                    <TableHead>결재 플로우</TableHead>
                    <TableHead className="w-[100px]">동작</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requestTypes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        요청 유형이 없습니다. 추가 버튼으로 등록하세요.
                      </TableCell>
                    </TableRow>
                  ) : (
                    requestTypes.map((rt, idx) => (
                      <TableRow key={rt.requestTypeId}>
                        <TableCell className="font-mono text-sm">{rt.requestTypeId}</TableCell>
                        <TableCell>{rt.displayName}</TableCell>
                        <TableCell>{DOMAIN_LABELS[rt.domain] ?? rt.domain}</TableCell>
                        <TableCell>
                          {serviceTypes.find((s) => s.serviceTypeId === rt.workflowServiceTypeId)?.displayName ?? rt.workflowServiceTypeId ?? '-'}
                        </TableCell>
                        <TableCell>
                          {approvalFlows.find((a) => a.approvalFlowId === rt.approvalFlowId)?.name ?? rt.approvalFlowId ?? '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openRequestTypeEdit(idx)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDeleteRequestType(idx)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>

          <Dialog open={isRequestTypeDialogOpen} onOpenChange={setIsRequestTypeDialogOpen}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingRequestTypeIndex !== null ? '요청 유형 편집' : '요청 유형 추가'}</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-2">
                  <Label className="col-span-1">요청 유형 ID</Label>
                  <Input
                    className="col-span-3"
                    value={requestTypeForm.requestTypeId}
                    onChange={(e) => setRequestTypeForm((f) => ({ ...f, requestTypeId: e.target.value }))}
                    placeholder="예: sr-general"
                    disabled={editingRequestTypeIndex !== null}
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-2">
                  <Label className="col-span-1">표시명</Label>
                  <Input
                    className="col-span-3"
                    value={requestTypeForm.displayName}
                    onChange={(e) => setRequestTypeForm((f) => ({ ...f, displayName: e.target.value }))}
                    placeholder="예: 일반 서비스 요청"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-2">
                  <Label className="col-span-1">도메인</Label>
                  <Select
                    value={requestTypeForm.domain}
                    onValueChange={(v: DomainType) => setRequestTypeForm((f) => ({ ...f, domain: v }))}
                  >
                    <SelectTrigger className="col-span-3">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(DOMAIN_LABELS) as DomainType[]).map((d) => (
                        <SelectItem key={d} value={d}>{DOMAIN_LABELS[d]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-4 items-center gap-2">
                  <Label className="col-span-1">카테고리</Label>
                  <Input
                    className="col-span-3"
                    value={requestTypeForm.category}
                    onChange={(e) => setRequestTypeForm((f) => ({ ...f, category: e.target.value }))}
                    placeholder="선택"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-2">
                  <Label className="col-span-1">설명</Label>
                  <Input
                    className="col-span-3"
                    value={requestTypeForm.description ?? ''}
                    onChange={(e) => setRequestTypeForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="선택"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-2">
                  <Label className="col-span-1">Workflow 서비스 유형</Label>
                  <Select
                    value={requestTypeForm.workflowServiceTypeId || '_none'}
                    onValueChange={(v) => setRequestTypeForm((f) => ({ ...f, workflowServiceTypeId: v === '_none' ? '' : v }))}
                  >
                    <SelectTrigger className="col-span-3">
                      <SelectValue placeholder="선택" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">없음</SelectItem>
                      {serviceTypes.map((s) => (
                        <SelectItem key={s.serviceTypeId} value={s.serviceTypeId}>{s.displayName}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-4 items-center gap-2">
                  <Label className="col-span-1">결재 플로우</Label>
                  <Select
                    value={requestTypeForm.approvalFlowId || '_none'}
                    onValueChange={(v) => setRequestTypeForm((f) => ({ ...f, approvalFlowId: v === '_none' ? '' : v }))}
                  >
                    <SelectTrigger className="col-span-3">
                      <SelectValue placeholder="선택" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">없음</SelectItem>
                      {approvalFlows.map((a) => (
                        <SelectItem key={a.approvalFlowId} value={a.approvalFlowId}>{a.name || a.approvalFlowId}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-4 items-start gap-2">
                  <Label className="col-span-1 pt-2">폼 필드 (JSON)</Label>
                  <textarea
                    className="col-span-3 min-h-[80px] rounded border bg-background px-3 py-2 text-sm"
                    value={JSON.stringify(requestTypeForm.form?.fields ?? [], null, 2)}
                    onChange={(e) => {
                      try {
                        const fields = JSON.parse(e.target.value || '[]');
                        setRequestTypeForm((f) => ({ ...f, form: { fields: Array.isArray(fields) ? fields : [] } }));
                      } catch {
                        // ignore invalid json while typing
                      }
                    }}
                    placeholder='[{"fieldId":"title","label":"제목","type":"text","required":true}]'
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsRequestTypeDialogOpen(false)}>취소</Button>
                <Button onClick={handleSaveRequestType}>저장</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
        <TabsContent value="domain-visibility" className="mt-4">
          <Card className="p-4">
            <p className="text-sm text-muted-foreground mb-4">
              각 Workflow 서비스 유형·요청 유형이 서비스 요청 / 장애 관리 / 변경 관리에서 노출될지 설정합니다. 변경 후 저장 버튼을 눌러주세요.
            </p>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[240px]">유형</TableHead>
                    <TableHead className="text-center">서비스 요청</TableHead>
                    <TableHead className="text-center">장애 관리</TableHead>
                    <TableHead className="text-center">변경 관리</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {serviceTypes.map((st, idx) => (
                    <TableRow key={st.serviceTypeId}>
                      <TableCell className="font-medium">
                        <span className="text-muted-foreground text-xs mr-1">[서비스 유형]</span>
                        {st.displayName}
                      </TableCell>
                      {(['servicerequest', 'incident', 'changerequest'] as DomainType[]).map((domain) => (
                        <TableCell key={domain} className="text-center">
                          <input
                            type="checkbox"
                            checked={(st.enabledInDomains ?? []).includes(domain)}
                            onChange={() => toggleServiceTypeDomain(idx, domain)}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {requestTypes.map((rt, idx) => (
                    <TableRow key={rt.requestTypeId}>
                      <TableCell className="font-medium pl-8">
                        <span className="text-muted-foreground text-xs mr-1">[요청 유형]</span>
                        {rt.displayName}
                      </TableCell>
                      {(['servicerequest', 'incident', 'changerequest'] as DomainType[]).map((domain) => (
                        <TableCell key={domain} className="text-center">
                          <input
                            type="checkbox"
                            checked={(rt.enabledInDomains ?? []).includes(domain)}
                            onChange={() => toggleRequestTypeDomain(idx, domain)}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {serviceTypes.length === 0 && requestTypes.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        먼저 Workflow 서비스 유형·Workflow 서비스를 등록하세요.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            <div className="mt-4">
              <Button onClick={saveDomainVisibility} disabled={!tenantId || isLoading}>
                변경 사항 저장
              </Button>
            </div>
          </Card>
        </TabsContent>
        <TabsContent value="approval-flows" className="mt-4">
          <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">결재 플로우를 정의하고 단계별 결재자·합의자·알림 대상을 설정합니다.</p>
              <Button size="sm" onClick={openApprovalFlowAdd} disabled={!tenantId || isLoading}>
                <Plus className="w-4 h-4 mr-1" />
                추가
              </Button>
            </div>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>플로우 ID</TableHead>
                    <TableHead>이름</TableHead>
                    <TableHead>단계 수</TableHead>
                    <TableHead className="w-[100px]">동작</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {approvalFlows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        결재 플로우가 없습니다. 추가 버튼으로 등록하세요.
                      </TableCell>
                    </TableRow>
                  ) : (
                    approvalFlows.map((af, idx) => (
                      <TableRow key={af.approvalFlowId}>
                        <TableCell className="font-mono text-sm">{af.approvalFlowId}</TableCell>
                        <TableCell>{af.name ?? '-'}</TableCell>
                        <TableCell>{(af.steps?.length ?? 0)}단계</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openApprovalFlowEdit(idx)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDeleteApprovalFlow(idx)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>

          <Dialog open={isApprovalFlowDialogOpen} onOpenChange={setIsApprovalFlowDialogOpen}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingApprovalFlowIndex !== null ? '결재 플로우 편집' : '결재 플로우 추가'}</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-2">
                  <Label className="col-span-1">플로우 ID</Label>
                  <Input
                    className="col-span-3"
                    value={approvalFlowForm.approvalFlowId}
                    onChange={(e) => setApprovalFlowForm((f) => ({ ...f, approvalFlowId: e.target.value }))}
                    placeholder="예: default-flow"
                    disabled={editingApprovalFlowIndex !== null}
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-2">
                  <Label className="col-span-1">이름</Label>
                  <Input
                    className="col-span-3"
                    value={approvalFlowForm.name ?? ''}
                    onChange={(e) => setApprovalFlowForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="표시용 이름"
                  />
                </div>
                <div className="col-span-4">
                  <Label className="mb-2 block">결재 단계</Label>
                  {(approvalFlowForm.steps ?? []).map((step, stepIdx) => (
                    <Card key={stepIdx} className="p-3 mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm">단계 {stepIdx + 1}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 text-destructive"
                          onClick={() => {
                            const steps = (approvalFlowForm.steps ?? []).filter((_, i) => i !== stepIdx);
                            setApprovalFlowForm((f) => ({ ...f, steps: steps.length ? steps : [{ approvers: [], consensus: [], notify: [] }] }));
                          }}
                        >
                          단계 삭제
                        </Button>
                      </div>
                      {(['approvers', 'consensus', 'notify'] as const).map((key) => (
                        <div key={key} className="mb-2">
                          <span className="text-xs text-muted-foreground capitalize">{key}</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(step[key] ?? []).map((entry: { kind: string; userId?: string; teamId?: string }, i: number) => (
                              <span
                                key={i}
                                className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs"
                              >
                                {entry.kind === 'team'
                                  ? (org?.teams?.find((t) => t.teamId === entry.teamId)?.teamName ?? entry.teamId)
                                  : (org?.users?.find((u) => u.userId === entry.userId)?.displayName ?? entry.userId)}
                                <button
                                  type="button"
                                  className="hover:text-destructive"
                                  onClick={() => {
                                    const arr = [...(step[key] ?? [])];
                                    arr.splice(i, 1);
                                    const steps = [...(approvalFlowForm.steps ?? [])];
                                    steps[stepIdx] = { ...steps[stepIdx], [key]: arr };
                                    setApprovalFlowForm((f) => ({ ...f, steps }));
                                  }}
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                            <Select
                              value="__add"
                              onValueChange={(v) => {
                                if (v === '__add') return;
                                const [kind, id] = v.split(':');
                                const entry = kind === 'team' ? { kind: 'team' as const, teamId: id } : { kind: 'user' as const, userId: id };
                                const arr = [...(step[key] ?? []), entry];
                                const steps = [...(approvalFlowForm.steps ?? [])];
                                steps[stepIdx] = { ...steps[stepIdx], [key]: arr };
                                setApprovalFlowForm((f) => ({ ...f, steps }));
                              }}
                            >
                              <SelectTrigger className="w-[180px] h-7 text-xs">
                                <SelectValue placeholder="+ 추가" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__add">+ 추가</SelectItem>
                                {org?.users?.filter((u) => u.status === 'active').map((u) => (
                                  <SelectItem key={`u-${u.userId}`} value={`user:${u.userId}`}>
                                    {u.displayName} ({u.userId})
                                  </SelectItem>
                                ))}
                                {org?.teams?.filter((t) => t.status === 'active').map((t) => (
                                  <SelectItem key={`t-${t.teamId}`} value={`team:${t.teamId}`}>
                                    팀: {t.teamName}
                                  </SelectItem>
                                ))}
                                {(!org?.users?.length && !org?.teams?.length) && (
                                  <SelectItem value="__none" disabled>조직 데이터 없음</SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ))}
                    </Card>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setApprovalFlowForm((f) => ({
                        ...f,
                        steps: [...(f.steps ?? []), { approvers: [], consensus: [], notify: [] }],
                      }))
                    }
                  >
                    + 단계 추가
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsApprovalFlowDialogOpen(false)}>취소</Button>
                <Button onClick={handleSaveApprovalFlow}>저장</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingIndex !== null ? 'Workflow 서비스 유형 편집' : 'Workflow 서비스 유형 추가'}</DialogTitle>
            <DialogDescription>유형 ID, 표시명, 설명, 연결 폴더, 노출 도메인을 입력하세요.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-2">
              <Label>유형 ID *</Label>
              <Input
                className="col-span-3"
                value={formData.serviceTypeId}
                onChange={(e) => setFormData((p) => ({ ...p, serviceTypeId: e.target.value.replace(/\s/g, '-') }))}
                placeholder="예: iam-users"
                disabled={editingIndex !== null}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-2">
              <Label>표시명 *</Label>
              <Input
                className="col-span-3"
                value={formData.displayName}
                onChange={(e) => setFormData((p) => ({ ...p, displayName: e.target.value }))}
                placeholder="예: 계정 관리"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-2">
              <Label>설명</Label>
              <Textarea
                className="col-span-3"
                value={formData.description || ''}
                onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                placeholder="선택"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-2">
              <Label>연결 폴더</Label>
              <Input
                className="col-span-3"
                value={formData.linkedFolder || ''}
                onChange={(e) => setFormData((p) => ({ ...p, linkedFolder: e.target.value }))}
                placeholder="예: services/iam-users"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-2">
              <Label>표시 순서</Label>
              <Input
                type="number"
                className="col-span-3"
                value={formData.displayOrder ?? 0}
                onChange={(e) => setFormData((p) => ({ ...p, displayOrder: parseInt(e.target.value, 10) || 0 }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              취소
            </Button>
            <Button onClick={handleSaveServiceType} disabled={!formData.serviceTypeId?.trim() || !formData.displayName?.trim()}>
              저장
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
